import {ApolloError} from "apollo-client";
import {ERROR_MESSAGE} from "./ConfigConstants";
import {GraphQLError} from "graphql";
import {FORBIDDEN_ERROR} from "../pages/lmdp/LmdpConstants";

/**
 * Prod Space API GW adds the statusCode path under statusCode attr of exception field.
 * @param statusCode - statusCode i.e 403, 404
 * @param exception - Apollo Client Error
 */
export function equalsProdSpaceError(statusCode: number, exception: ApolloError): boolean {
    return exception?.graphQLErrors?.some(
        (ex) => {
            if (ex && ex.extensions && ex.extensions["exception"] && ex.extensions["exception"]["statusCode"]) {
                return ex.extensions["exception"]["statusCode"] === statusCode;
            }
            return false;
        }) || false;
}

/**
 * Booking Svc API GW sends the exception name as prefix of the error message.
 * @param exceptionName i.e. "NotFoundException"
 * @param exception - Apollo Client Error
 */
export function equalsUserSpaceError(exceptionName: string, exception: ApolloError): boolean {
    return exception?.message?.includes(exceptionName) || false;
}

/**
 * @param exception i.e. "NotFoundException"
 */
export function equalsAuthV2AccessDeniedError(error: ApolloError): boolean {
  return error?.graphQLErrors?.some(error => error.message === FORBIDDEN_ERROR) || false;
}

/**
 * Booking Svc API GW sends the exception name as prefix of the error message.
 * @param exceptionName i.e. "NotFoundException"
 * @param errors - Apollo Client Error
 */
export function equalsUserSpaceGraphqlError(exceptionName: string, errors: ReadonlyArray<GraphQLError>): boolean {
    return errors?.some(error => error?.message?.includes(exceptionName)) || false;
}

/**
 * This method performs a check if the error faced by User is Access Denied or not.
 * If User isn't part of any LDAP group of Argo, he will face "Missing credentials in config" error from Graphql Lambda and
 * If User is part of at least one of Argo LDAP groups, but isn't added in Shipments LDAP group, then he will get "is not authorized" error message from Graphql Lambda.
 * @param error - Apollo Error
 */
export function isUserAccessDenied(error: ApolloError): boolean {
    if (!error || !error.graphQLErrors) {
        return false;
    }
    if (equalsProdSpaceError(ERROR_MESSAGE.ACCESS_DENIED_STATUS_CODE, error) || equalsUserSpaceError(ERROR_MESSAGE.USER_SPACE_ERRORS.ACCESS_DENIED_ERR.NAME, error) || equalsAuthV2AccessDeniedError(error)) {
        return true;
    }
    const errorMessage = error.graphQLErrors[0]?.message;
    if (errorMessage) {
        return errorMessage.includes("Missing credentials in config") || errorMessage.includes("is not authorized");
    }
    return false;
}

/**
 * This method performs a check if the exception message faced by User is Access Denied or not.
 * If User is part of at least one of Argo LDAP groups, but isn't added in Shipments LDAP group, then the user will get "is not authorized" error message from Graphql Lambda.
 * @param error - Apollo Error
 */
export function isUserAccessDeniedMessage(exception: string | undefined): boolean {
    if (exception?.includes(ERROR_MESSAGE.USER_SPACE_ERRORS.ACCESS_DENIED_ERR.NAME)) {
        return true;
    }
    return false;
}

/**
 * This method performs a check if the error faced by user is client error or not.
 * If user has provided invalid input to a filed, he will face "InvalidInputException" error from Graphql Lambda
 * @param error - Apollo Error
 */
export function isInvalidUserInput(error: ApolloError): boolean {
    if (!error || !error.graphQLErrors) {
        return false;
    }
    if (equalsUserSpaceError(ERROR_MESSAGE.USER_SPACE_ERRORS.INVALID_INPUT_ERR.NAME, error)){
        return true;
    }
    return false;
}

/**
 * This method parses error received from Graphql Lambda which has a format :
 * "ExceptionName: <request object that resulted in failure> with message: <error message>"
 * @param error - Apollo Error
 */
export function parseErrorMessage(error: ApolloError, parser: string): string {
    if (!error || !error.graphQLErrors) {
        return "";
    }
    const errorMessage = error.graphQLErrors[0]?.message;
    return errorMessage.split(parser)[1];
}

export function parseException(clientErrorMessage: string, exception?: string): string | undefined {
    if (exception?.includes(ERROR_MESSAGE.USER_SPACE_ERRORS.ACCESS_DENIED_ERR.NAME) || exception === ERROR_MESSAGE.GENERIC_ACCESS_DENIED_ERROR_MESSAGE) {
        return ERROR_MESSAGE.GENERIC_ACCESS_DENIED_ERROR_MESSAGE;
    }
    return exception !== undefined ? clientErrorMessage : undefined;
}